// https://js.devexpress.com/Documentation/ApiReference/UI_Components/dxDataGrid
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';
import DataGrid, {
  Column,
  Export,
  Paging,
  Selection,
  FilterRow,
  Pager,
  ColumnChooser,
  HeaderFilter,
  Summary,
  TotalItem,
  Item,
  Toolbar,
  Lookup,
  ColumnFixing,
  Sorting,
  Grouping,
  GroupItem,
  MasterDetail,
  Button,
  Scrolling,
  Editing
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Component } from 'devextreme-react/core/component';
import { useDispatch, useSelector } from 'react-redux';
import { MdOutlineAddCircleOutline, MdPayments } from 'react-icons/md';
import { HiOutlineCog } from 'react-icons/hi';
import TableSettingModal from './TableSettingModal';
import { RootState } from 'app/store';
import { TTableColumnSetting, updateColumnSetting, updateOpeSettingTable } from '@slice/user.slice';

// import 'devextreme/dist/css/dx.carmine.css';
// import 'devextreme/dist/css/dx.contrast.css';
// import 'devextreme/dist/css/dx.dark.css';
// import 'devextreme/dist/css/dx.darkmoon.css';
// import 'devextreme/dist/css/dx.darkviolet.css';
// import 'devextreme/dist/css/dx.greenmist.css';
// import 'devextreme/dist/css/dx.light.css';
import { ColumnResizeMode } from 'devextreme/ui/data_grid_types';
import { MINPAGESIZE } from 'global/Global';
import { DeleteOutlined, DeliveredProcedureOutlined, SyncOutlined } from '@ant-design/icons';
import SubDetailTable from './SubDetailTable';
import { Tooltip } from 'antd';
import { updateReqReGetCustomer } from '@slice/orderManager.slice';

// Style component

const DataGridStyle = styled(DataGrid)<{
  gridHeight?: number;
  onRowHeight?: number;
  wrapText?: boolean;
  wrapTextData?: boolean;
  fontSize?: string;
  fontStyle?: string;
  disableHover?: boolean;
  theme?: any;
  isUseStyle?: any; // nếu dùng style ngoài thì k cần set style trong
}>`
  &.smallGrid {
    max-height: 325px;
  }
  &.mediumGrid {
    max-height: 442px;
  }
  &.fullScreenGrid {
    max-height: 552px;
  }
  &.gridContainer {
    max-height: calc(${props => props.gridHeight}vh - 78px);
    font-family: Roboto !important;
  }

  // .dx-gridbase-container > .dx-datagrid-rowsview.dx-scrollable {
  // }

  .dx-datagrid-focus-overlay {
    border: none !important;
  }
  .dx-datagrid-headers {
    border-bottom: unset;
  }
  .dx-widget input {
    font-family: Roboto !important;
  }

  .dx-texteditor-buttons-container {
    color: ${props => props.theme.colors!.text};
  }
  .dx-datagrid-filter-row .dx-datagrid-group-space {
    border-bottom: 2px solid #ddd !important;
  }
  .dx-toolbar-after .dx-toolbar-item .dx-toolbar-item-content {
    &:hover {
      color: ${props => props.theme.select!.text};
    }
  }
  .dx-button-mode-contained {
    &:hover {
      background-color: ${props => props.theme.colors!.dimBackground};
      color: ${props => props.theme.colors!.text};
    }
  }
  .dx-button-mode-contained .dx-icon {
    color: unset;
  }
  .dx-dropdowneditor-button.dx-button-mode-contained {
    color: ${props => props.theme.colors!.text};
  }
  .dx-datagrid-headers .dx-header-row {
    background-color: ${props => props.theme.colors!.hoverTableBg};
    color: ${props => props.theme.colors!.hoverTableText};
    font-size: ${props => props.fontSize} !important;
    text-transform: capitalize;
    > td {
      vertical-align: middle !important;
      text-align: center !important;
      .dx-datagrid-text-content {
        white-space: ${props => (props.wrapText ? 'break-spaces !important' : 'nowrap !important')};
      }
    }
    .dx-col-fixed {
      background-color: unset !important;
    }
  }

  .dx-datagrid-headers .dx-datagrid-filter-row .dx-command-expand {
    z-index: 0 !important;
  }

  .dx-row .dx-editor-cell .dx-texteditor-container {
    font-size: ${props => props.fontSize} !important;
    font-family: Roboto !important;
  }

  .dx-row.dx-data-row.dx-column-lines.dx-state-hover td {
    background-color: ${props =>
      props.disableHover === undefined || props.disableHover === true
        ? props.theme.colors!.hoverTableBg
        : '#EDEDED'} !important;
    color: ${props =>
      props.disableHover === undefined || props.disableHover === true
        ? props.theme.colors!.hoverTableText
        : 'black'} !important;
    .icon_table {
      color: white;
    }
    &.custom_cell-warning {
      background-color: #ff0 !important;
      color: rgba(0, 0, 0, 1) !important;
    }
    &.custom_cell-error {
      background-color: #dd4b39 !important;
    }
    &.dx-col-fixed {
      background-color: none !important;
    }
  }
  .dx-datagrid-rowsview {
    font-size: ${props => props.fontSize};
    font-style: ${props => props.fontStyle};
  }
  .dx-datagrid-total-footer {
    font-size: ${props => props.fontSize};
  }
  .dx-datagrid-pager {
    font-size: ${props => props.fontSize};
  }
  .dx-datagrid {
    line-height: initial;
  }
  .dx-datagrid-rowsview .dx-row {
    height: ${props => props.onRowHeight + 'px !important'};
    td {
      vertical-align: middle !important;
      min-width: 40px !important;
      height: ${props => props.onRowHeight + 'px !important'};
      white-space: ${props => (props.wrapTextData ? 'break-spaces !important' : 'nowrap !important')};
      div {
        white-space: ${props => (props.wrapTextData ? 'break-spaces !important' : 'nowrap !important')};
      }
    }
    .dx-col-fixed {
      background-color: unset !important;
    }
    .dx-footer-row {
      td > div {
        width: max-content !important;
      }
    }
    .dx-datagrid .dx-row-alt > td,
    .dx-datagrid .dx-row-alt > tr > td {
      background-color: ${props => (props.isUseStyle ? 'unset' : '#f5f5f5')};
    }
    .dx-datagrid-headers .dx-datagrid-table .dx-row > td {
      border-bottom: 2px solid #ddd;
    }
  }
`;

// Interface
interface IRowTableEvent {
  onClick?: (e: any, row: any, rowIndex: any) => any;
  onDbClick?: (e: any, row: any, rowIndex: any) => any;
  onMouseEnter?: (e: any, row: any, rowIndex: any) => any;
}

type TTypeAlign = 'right' | 'center' | 'left';
type TTSummaryCol = 'count' | 'sum' | 'avg' | 'max';
type TDataType = 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime';
export interface IColumnTable {
  dataField?: string;
  alignment?: TTypeAlign;
  caption?: string;
  width?: number;
  reSizeable?: boolean;
  minWidth?: number;
  allowFiltering?: boolean;
  allowSorting?: boolean;
  isSumCol?: boolean;
  showInColumn?: string;
  isSumFooter?: boolean;
  sumCol?: string;
  visible?: boolean;
  visibleWidth?: any;
  allowHiding?: boolean;
  showInColumnChooser?: boolean;
  headerFilter?: boolean;
  defaultSortOrder?: 'asc' | 'desc' | '';
  defaultSortIndex?: number;
  dataType?: TDataType;
  displayFormat?: any | string;
  valueFormat?: 'currency' | 'sum';
  formatCustomSum?: any;
  format?: string;
  summaryType?: TTSummaryCol;
  customizeText?: any;
  fixed?: boolean;
  cellRender?: any;
  cellTemplate?: any;
  groupCellRender?: any;
  showWhenGrouped?: boolean;
  headerRender?: any;
  lookupFilter?: boolean;
  lookupFilterNvThucHien?: boolean;
  lookupAt?: string;
  sourceLookup?: any;
  dataFilter?: Array<any>;
  type?: 'adaptive' | 'buttons' | 'detailExpand' | 'groupExpand' | 'selection' | 'drag';
  groupIndex?: number;
  showInGroupFooter?: boolean;
  childHeader?: Array<IColumnTable>;
  editCellRender?: (data: any) => any;
}

interface ITable {
  onInitialized?: any;
  allowUpdating?: boolean;
  allowDeleting?: boolean;
  allowAdding?: boolean;
  onRowUpdated?: (e: any) => void;
  onRowUpdating?: (e: any) => void;
  modeEditing?: 'row' | 'cell' | 'batch' | 'form';

  exportToImport?: boolean;
  paging?: any;
  scrolling?: any;
  selection?: any;
  summary?: any;
  onSelectionChanged?: any;
  selectedRowKeys?: any;
  selectedRowKeysV2?: any;

  columns: Array<IColumnTable>;
  data: Array<any>;
  total?: number;
  tableName: string;
  tableId: string;
  keyExpr?: string;
  repaintChangesOnly?: boolean;
  downHeight?: number; // down with unit is vh
  onRowHeight?: number;
  hidden?: boolean;
  isHiddenFilter?: boolean;
  isMultipleHightlight?: boolean;
  rowEvents?: IRowTableEvent;
  allowEditing?: boolean;
  fileNameExport?: string;
  isStriped?: boolean;
  allowSelect?: boolean;
  isHiddenToolbar?: boolean;
  columnFix?: boolean;
  columnResizingWidget?: boolean | string;
  columnMinWidth?: number;
  showBorders?: boolean;
  columnHidingEnabled?: boolean;
  showRowLines?: boolean;
  showColumnHeaders?: boolean;
  allowColumnResizing?: boolean;
  allowExcel?: boolean;
  columnAutoWidth?: boolean;
  customBeforeToolbar?: React.ReactNode | any;
  customCenterToolbar?: any;
  isModal?: 'smallGrid' | 'mediumGrid' | 'fullScreenGrid';
  expandedAll?: boolean;
  hiddenAddButton?: boolean;
  showImportExcel?: boolean;
  showExportExcel?: boolean;
  showReloadCustomer?: boolean;
  setOpenImportXls?: any;
  hiddenSetting?: boolean;
  reload?: boolean;
  thuTien?: boolean;
  wallet?: boolean;
  isAdmin?: boolean;
  listWallet?: number;
  listThuTien?: number;
  isSubRowRender?: boolean;
  wrapText?: boolean;
  wrapTextData?: boolean;
  isHiddenPaging?: boolean;
  getCellProps?: (value: any) => any;
  onClickAddRecord?: () => any;
  oncClickReload?: () => any;
  onClickThuTien?: () => any;
  onClickCapPhat?: () => any;
  onClickDelete?: () => any;
  handleExportExcel?: () => any;
  onDBClickRow?: (data: any) => any;
  onClickRow?: (data: any) => any;
  onRightClickRow?: (data: any) => any;
  onRowPreparedStyle?: (data: any) => any;
  onCellPrepared?: (data: any) => any;
  SubDetailCallBack?: (props: any) => any;
  onCallbackComponent?: (data: any) => any;
  sendRequireExportData?: (data: any) => any;
  resExportData?: (data: any) => any;
  onPageInfo?: (data: any) => any;
  getCallbackExport?: (data: any) => any;
  timeExport?: string;
  activeNextColumn?: boolean;
  disableHover?: boolean;
  hoverStateColor?: boolean;
  useScrollNative?: boolean | undefined; // cấu hình cho table phân quyền k bị giật
  enableLoadingPanel?: boolean;
  noHiddenPaging?: boolean;
  showPageSizeSelector?: boolean;
  showInfo?: boolean;
  showNavigationButtons?: boolean;
  summaryPage?: number;
  currentPage?: number;
  isResetAllFilter?: boolean;
  customFilterValue?: any[];
  minPageSize?: number;
  onEditorPreparing?: (e: any) => void;
  // thu tiền
  collectMoney?: boolean;
  count?: number;
  // new
  type?: string;
  subColumns?: any;

  // isCustomRowStyle?: boolean;
}
/** Phần khởi tạo và hiển thị bảng báo cáo được cấu hình hoàn chỉnh cho tất cả các báo cáo*/
export default function TableDevReport(props: ITable) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [minPageSize, setMinPageSize] = useState<number>(MINPAGESIZE);
  const tableSetting = useSelector((state: RootState) => state.user.tableSetting);
  const selectedTheme = useSelector((state: RootState) => state.select.themeSelected);

  // ref
  const visibleColumnsAmountion = useRef<number>(-1);
  const updateSetting = useRef<any>(undefined);
  const refKeySelect = useRef<any>('');
  let timeOutClick: any = null;

  const dataGrid = useRef<any>(undefined);
  const setAlternatingRowsBackground = (gridCell: any, excelCell: any, workbook: any, worksheet: any) => {
    // need convert pic to base64 value to add in file excel
    // if (gridCell.column.dataField === 'Picture') {
    //   excelCell.value = undefined;

    //   const image = workbook.addImage({
    //     base64: gridCell.value,
    //     extension: 'png',
    //   });

    //   worksheet.getRow(excelCell.row).height = 90;
    //   worksheet.addImage(image, {
    //     tl: { col: excelCell.col - 1, row: excelCell.row - 1 },
    //     br: { col: excelCell.col, row: excelCell.row },
    //   });
    // }
    // || gridCell.rowType === 'data'
    if (gridCell.rowType === 'header') {
      // excelCell.border = {
      //   top: { style: 'thin' },
      //   left: { style: 'thin' },
      //   bottom: { style: 'thin' },
      //   right: { style: 'thin' },
      // };
      // excelCell.height = 40;
      // if (excelCell.fullAddress.row % 2 === 0) {
      //   excelCell.fill = {
      //     type: 'pattern',
      //     pattern: 'solid',
      //     fgColor: { argb: 'ffffff' },
      //     bgColor: { argb: 'ffffff' },
      //   };
      // }
    }
    excelCell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
      diagonal: { up: true, down: true }
    };
  };

  const onExporting = (e: any) => {
    if (props.exportToImport) {
      const enu: any = new Component(e);
      if (props.data?.length === 0) {
        console.log('Thông báo ra onExporting');
        enu.props.cancel = true;
        return;
      }
      if (props.onCallbackComponent) {
        props.sendRequireExportData && props.sendRequireExportData(e);
        enu.props.cancel = true;
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      exportDataGrid({
        component: enu.props.component,
        worksheet,
        keepColumnWidths: true,
        // CHỈNH SỬA 1: Đưa bảng lên hàng 1, cột 1
        topLeftCell: { row: 1, column: 1 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell, workbook, worksheet);
        }
      })
        .then((cellRange: any) => {
          // CHỈNH SỬA 2: Xóa toàn bộ phần logic chèn headerRow (Row 2) và rowTime (Row 3)
          // Chỉ giữ lại định dạng cho hàng Header của Table (nay là Row 1)

          const row = worksheet.getRow(1); // Header của table bây giờ là Row 1
          row.height = 30;
          row.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          row.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          // Giữ lại Footer nếu bạn vẫn muốn hiển thị Copyright ở cuối bảng
          const amountColumn = props.columns.reduce((sum, item) => {
            if (item.childHeader && item.childHeader?.length !== 0) return sum + item.childHeader?.length;
            return sum + 1;
          }, 0);

          const footerRowIndex = cellRange.to.row + 1; // Sát ngay dưới bảng
          const footerRow = worksheet.getRow(footerRowIndex);
          worksheet.mergeCells(footerRowIndex, 1, footerRowIndex, amountColumn);

          footerRow.getCell(1).value = 'copyright@2023 / ' + window.location.hostname;
          footerRow.getCell(1).font = { color: { argb: 'BFBFBF' }, italic: true };
          footerRow.getCell(1).alignment = { horizontal: 'right' };
        })
        .then(() => {
          workbook.xlsx.writeBuffer().then((buffer: any) => {
            saveAs(
              new Blob([buffer], { type: 'application/octet-stream' }),
              `${props.fileNameExport ? t(props.fileNameExport) : 'FileExport'}.xlsx`
            );
          });
        });
      enu.props.cancel = true;
    } else {
      const enu: any = new Component(e);
      if (props.data?.length === 0) {
        // Toast(t('Table.Export.MessageNoData'), ToastType.INFO);
        console.log('Thông báo ra onExporting');
        enu.props.cancel = true;
        return;
      }
      if (props.onCallbackComponent) {
        // console.log('không xuất đc khi chưa load xong dữ liệu', props.onCallbackComponent);
        props.sendRequireExportData && props.sendRequireExportData(e);
        enu.props.cancel = true;
        return;
      }

      const timeSelect = moment().format('HH:mm:ss DD/MM/YYYY');
      const workbook = new Workbook();

      const worksheet = workbook.addWorksheet('Sheet1');
      const amountColumn = props.columns.reduce((sum, item, array) => {
        if (item.childHeader && item.childHeader?.length !== 0) return sum + item.childHeader?.length;
        return sum + 1;
      }, 0);

      exportDataGrid({
        component: enu.props.component,
        worksheet,
        keepColumnWidths: true,
        // autoFilterEnabled: true,
        // mergeColumnFieldValues: true,
        topLeftCell: { row: 4, column: 1 },
        // onRowExporting: (e: any) => {
        //   console.log(e);
        //   // const isHeader = e.rowCells[0].text === 'Picture';
        //   // if (!isHeader) {
        //   //   e.rowHeight = 40;
        // },

        // customDrawCell: (e: any) => {
        //   console.log(e);
        //   // if (e.gridCell.rowType === 'data' && e.gridCell.column.dataField === 'Picture') {
        //   //   doc.addImage(e.gridCell.value, 'PNG', e.rect.x, e.rect.y, e.rect.w, e.rect.h);
        //   //   e.cancel = true;
        // },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell, workbook, worksheet);
        }
      })
        .then((cellRange: any) => {
          // title table

          const headerRow = worksheet.getRow(2);
          const row = worksheet.getRow(4);
          row.height = 30;
          row.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          row.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
            diagonal: { up: true, down: true }
          };
          // header
          headerRow.height = 40;
          // const headerRowTable = worksheet.getRow(2);
          // headerRowTable.height = 15;
          worksheet.mergeCells(2, 1, 2, amountColumn);
          headerRow.getCell(1).value = `${props.fileNameExport ? t(props.fileNameExport) : 'Tiêu đề FileExport'}`;
          headerRow.getCell(1).font = { name: 'Segoe UI Light', size: 20, bold: true };
          headerRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

          worksheet.mergeCells(3, 1, 3, amountColumn);
          const rowTime = worksheet.getRow(3);

          rowTime.getCell(1).value = `timeExport: ${timeSelect}`;

          rowTime.getCell(1).font = { name: 'Segoe UI Light', size: 15, bold: true };

          rowTime.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

          // footer
          const footerRowIndex = cellRange.to.row + 2;
          const footerRow = worksheet.getRow(footerRowIndex);
          worksheet.mergeCells(footerRowIndex, 1, footerRowIndex, amountColumn);

          footerRow.getCell(1).value = 'copyright@2023 / ' + window.location.hostname;
          footerRow.getCell(1).font = { color: { argb: 'BFBFBF' }, italic: true };
          footerRow.getCell(1).alignment = { horizontal: 'right' };
        })
        .then(() => {
          workbook.xlsx.writeBuffer().then((buffer: any) => {
            saveAs(
              new Blob([buffer], { type: 'application/octet-stream' }),
              `${props.fileNameExport ? t(props.fileNameExport) : 'FileExport'}.xlsx`
            );
          });
        });
      enu.props.cancel = true;
    }
  };

  const onClickSettingTable = () => {
    // console.log('onClickSettingTable');
    dispatch(updateOpeSettingTable(true));
  };

  const handleProcessSettingSave = useCallback((settingUpdate: TTableColumnSetting) => {
    // xử lý khi kéo quá nhanh (làm chậm quá trình up lên)
    dispatch(updateColumnSetting(settingUpdate));
  }, []);

  useEffect(() => {
    if (dataGrid.current && props.onRightClickRow) {
      dataGrid.current._element &&
        dataGrid.current._element.addEventListener('contextmenu', e => {
          contextmenu(e);
        });
    }

    return () => {
      if (dataGrid.current) {
        // console.log('chuyển table 1', dataGrid.current.instance);
        dataGrid.current._element &&
          dataGrid.current._element.removeEventListener('contextmenu', e => {
            contextmenu(e);
          });
        // dataGrid.current.instance.refresh();
        // dataGrid.current.instance.repaintRows();
        dataGrid.current = null;
      }
      if (updateSetting.current) {
        // console.log('update sizecols unmount');
        dispatch(updateColumnSetting(updateSetting.current));
        updateSetting.current = undefined;
      }
    };
  }, []);

  //   chrome.runtime.onMessage.addListener(function(request) {
  //     // The following is your async call
  //     logout();
  //     // Don't forget the following code
  //     return true;
  // });

  //   window.addEventListener('beforeunload', function() {
  //     // chrome.runtime.sendMessage({ info: "Here is the info you would like to pass to background page"});
  // });

  window.onbeforeunload = function () {
    // console.log('đang nhấn f5 nè lưu đi cha nội');
    if (updateSetting.current) {
      console.log('update sizecols unmount');
      dispatch(updateColumnSetting(updateSetting.current));
      updateSetting.current = undefined;
    }
  };

  const onContentReady = (elmt: any) => {
    // console.log(
    //   'onContentReady',
    //   elmt,
    //   props.data
    //   // elmt.component.getVisibleColumns()
    //   // elmt.component._controllers.columns._columns
    //   // elmt.component.pageSize(),
    //   // elmt.component.pageIndex(),
    //   // elmt.component.pageCount(),
    //   // elmt.component.getVisibleRows()
    // );
    // elmt.component._controllers.export._actions.onExporting(); // hàm xuất nhanh excel nè muốn dùng thì alo nhé
    // test xuất excel từ yc bên ngoài vào
    // const e = {
    //   cancel: true,
    //   component: elmt.component,
    //   element: elmt.element,
    //   fileName: 'DataGrid',
    //   format: 'xlsx',
    //   rtlEnabled: false,
    //   selectedRowsOnly: false
    // };
    // onExporting(e);

    props.getCallbackExport && props.getCallbackExport(elmt.component._controllers.export._actions);
    props.onPageInfo &&
      props.onPageInfo({
        pageCount: elmt.component.pageCount(),
        pageIndex: elmt.component.pageIndex(),
        pageSize: elmt.component.pageSize()
      });
    props.onCallbackComponent && props.onCallbackComponent(elmt.component);
  };
  const onColumnsChanging = (elmt: any) => {
    console.log('onColumnResize', elmt, visibleColumnsAmountion.current, elmt.component.getVisibleColumns().length);

    if (elmt.columnIndex >= 0 && elmt.component.getVisibleColumns().length > 1) {
      // ///// size
      if (
        visibleColumnsAmountion.current == elmt.component.getVisibleColumns().length ||
        visibleColumnsAmountion.current == -1
      ) {
        // cập nhật cho width cột

        const columnsSize = elmt.component._controllers.columns._columns.map(
          (column: any, index: number) => column.width && column.visibleWidth != false && column.width
        );
        // // only size width

        const columnHidden = tableSetting.tableColumnSetting.find(
          item => item.tableName == props.tableId
        )?.columnHidden;
        const hiddenAuto = tableSetting.tableColumnSetting.find(item => item.tableName == props.tableId)?.hiddenAuto;
        const tableSettingUpdateWidth = {
          tableName: props.tableId,
          columnHidden: columnHidden ? columnHidden : [],
          columnSize: columnsSize,
          hiddenAuto: hiddenAuto ? hiddenAuto : false
        };
        // console.log('columns size result', columnsSize);
        if (tableSetting.autoSaveColumnWidth) updateSetting.current = tableSettingUpdateWidth;

        visibleColumnsAmountion.current = elmt.component.getVisibleColumns().length;
        return 0;

        // tuy nhiên chỉ lưu tạm khi nào đổi chức năng mới lưu lại
      } else {
        visibleColumnsAmountion.current = elmt.component.getVisibleColumns().length;

        // process
        let isAutoWidth = 0;
        let isAutoWidthHidden = 0;
        // const columnsPros = elmt.component._controllers.columns._columns;

        const columnsHidden = elmt.component._controllers.columns._columns
          .map((column: any, index: number) => {
            if (!column.width) {
              isAutoWidth += 1;
              if (!column.visible) isAutoWidthHidden += 1;
            }
            if (!column.visible) return column.dataField;
            return undefined;
          })
          .filter((itemF: any) => itemF !== undefined);

        // console.log('columns hidden result', columnsHidden, isAutoWidth, isAutoWidthHidden);

        const columnSize = tableSetting.tableColumnSetting.find(item => item.tableName == props.tableId)?.columnSize;
        const tableSettingUpdateHide = {
          tableName: props.tableId,
          columnHidden: columnsHidden,
          columnSize: columnSize ? columnSize : [],
          hiddenAuto: isAutoWidthHidden > 0 && isAutoWidth == isAutoWidthHidden ? true : false
        };
        tableSetting.autoSaveColumnHidden && handleProcessSettingSave(tableSettingUpdateHide);
        // setUpdateSetting(tableSettingUpdateHide);
      }
    }
    visibleColumnsAmountion.current = elmt.component.getVisibleColumns().length;
  };

  let prevClickKey: any = undefined;
  // onMouse row event
  const onMouseRowEvent = (e: any) => {
    if (!prevClickKey || prevClickKey !== e.component.getRowIndexByKey(e.key)) {
      prevClickKey = e.component.getRowIndexByKey(e.key);
      // console.log('key sau', e.data);
      // xử lý với key đã mouse..
    }
  };

  // on DbClick row event
  const onDbClickRowEvent = (e: any) => {
    // console.log('onDbClickRowEvent', e.component.getRowIndexByKey(e.key));
    props.onDBClickRow(e.data);
  };

  // click này dùng để trả sự kiện chỉ 1 lần click và dùng dạng này để chặn click liên tục sẽ k nhận click
  // sẽ khác với dạng bắt sự kiện khi dùng useEffect
  // const [keySelect, setKeySelect] = useState<any>('');
  // const onClickRowEvent = (e: any) => {
  //   if (props.allowSelect) {
  //     if (keySelect !== e.key) {
  //       props.onClickRow && props.onClickRow(e.data);
  //       props.allowSelect && setKeySelect(e.key);
  //       return 0;
  //     }
  //     props.onClickRow && props.onClickRow([]);
  //     setKeySelect('');
  //     return;
  //   }
  //   if (props.onClickRow) {
  //     props.onClickRow && props.onClickRow(e.data);
  //   }
  // };

  const onClickRowEvent = (e: any) => {
    // console.log('onClickRowEvent', e);
    if (refKeySelect.current !== e.key) {
      props.allowSelect && (refKeySelect.current = e.key);
      clearTimeout(timeOutClick);
      timeOutClick = setTimeout(() => {
        props.onClickRow && props.onClickRow(e.data);
      }, 500);
      return 0;
    }
    // xoá timeout coi như k click..
    console.log('xoá click');
    refKeySelect.current = null;
    clearTimeout(timeOutClick);
    timeOutClick = null;
  };

  // click này dùng để lưu select click các dòng hiện k dùng
  // const [keySelect, setKeySelect] = useState<string>('');
  // const onClickRowEvent = (e: any) => {
  //   if (keySelect !== e.key) {
  //     props.onClickRow && props.onClickRow(e.data);
  //     props.allowSelect && setKeySelect(e.key);
  //     return 0;
  //   }
  //   props.onClickRow && props.onClickRow([]);
  //   setKeySelect('');
  // };

  const onContextMenuPreparing = (e: any) => {
    // console.log('e rightC', e);
    e?.row?.data && props.onRightClickRow(e.row.data);
    // if (e.type === "click") {
    //   console.log("Left click");
    // } else if (e.type === "contextmenu") {
    //   console.log("Right click");
    // }
  };

  const contextmenu = (e: any) => {
    if (e.srcElement?.id === '') {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  // <p onClick={onContextMenuPreparing} onContextMenu={onContextMenuPreparing}>
  //   click me
  // </p>

  /** hàm xử lý format đưa data qua cho table con phụ */
  // const SubDetailCallBack = (propsSub: any, type: string) => {
  //   // if (type === 'error') {
  //   console.log('props sub table', propsSub);
  //   if (type === 'error') {
  //     const handlings = propsSub.data.data.handlings || [];
  //     const subColumns = [
  //       {
  //         accessor: 'index',
  //         Header: 'STT',
  //         width: 50
  //       },
  //       {
  //         accessor: 'handlingText',
  //         allowSorting: false,
  //         Header: t('OrganizedSales.OrderManager.AllOrder.Handling')
  //         // minWidth: 120,
  //         // alignment: 'right',
  //         // sumCol: 'handlingText',
  //         // isSumFooter: true,
  //         // isSumCol: true,
  //         // displayFormat: 'SL: {0} Cách xử lý'
  //       },
  //       {
  //         accessor: 'count',
  //         allowSorting: false,
  //         Header: t('OrganizedSales.OrderManager.AllOrder.CountHandling')
  //         // minWidth: 120,
  //         // alignment: 'right',
  //         // sumCol: 'count',
  //         // isSumFooter: true,
  //         // isSumCol: true,
  //         // displayFormat: 'SL: {0} Cách xử lý'
  //       }
  //     ];

  //     const subData = (handlings || []).map((handling: any, idx: number) => ({
  //       ...handlings,
  //       index: idx + 1,
  //       handlingText: handling.handlingText,
  //       count: handling.count

  //       // Có thể thêm field khác tùy ý
  //     }));

  //     return <SubDetailTable tileTable={'titleTable'} gridHeight={200} data={subData} columns={subColumns || []} />;
  //   } else if (type === 'handling') {
  //     const errors = propsSub.data.data.errors || [];
  //     const subColumns = [
  //       {
  //         accessor: 'index',
  //         Header: 'STT',
  //         width: 50
  //       },
  //       {
  //         accessor: 'errorName',
  //         allowSorting: false,
  //         Header: t('OrganizedSales.OrderManager.AllOrder.NameError')
  //         // minWidth: 120,
  //         // alignment: 'right',
  //         // sumCol: 'handlingText',
  //         // isSumFooter: true,
  //         // isSumCol: true,
  //         // displayFormat: 'SL: {0} Cách xử lý'
  //       },
  //       {
  //         accessor: 'count',
  //         allowSorting: false,
  //         Header: t('OrganizedSales.OrderManager.AllOrder.CountHandling')
  //         // minWidth: 120,
  //         // alignment: 'right',
  //         // sumCol: 'count',
  //         // isSumFooter: true,
  //         // isSumCol: true,
  //         // displayFormat: 'SL: {0} Cách xử lý'
  //       }
  //     ];

  //     const subData = (errors || []).map((error: any, idx: number) => ({
  //       ...errors,
  //       index: idx + 1,
  //       errorName: error.errorName,
  //       count: error.count

  //       // Có thể thêm field khác tùy ý
  //     }));

  //     return <SubDetailTable tileTable={'titleTable'} gridHeight={200} data={subData} columns={subColumns || []} />;
  //   } else if (props.type === 'taoCongDuNoKH') {
  //     const congDuNo = propsSub.data.subData || [];

  //     const subColumns = [
  //       {
  //         accessor: 'index',
  //         Header: 'STT',
  //         width: 50
  //       },
  //       {
  //         accessor: 'saleAccount',
  //         allowSorting: false,
  //         Header: t('OrganizedSales.OrderManager.AllOrder.NameError')
  //       }
  //     ];
  //     console.log({ congDuNo });

  //     const subData = (congDuNo || []).map((congdu: any, idx: number) => ({
  //       ...congdu,
  //       index: idx + 1,
  //       saleAccount: congdu.saleAccount

  //       // Có thể thêm field khác tùy ý
  //     }));
  //     <SubDetailTable tileTable={''} gridHeight={200} data={subData} columns={subColumns || []} />;
  //   }
  // };

  const SubDetailCallBack = (propsSub: any) => {
    // console.log('props sub table', props.subColumns, propsSub.data.data.subData);
    if (props.type === 'thutienkythuat') {
      const subData = propsSub.data.details || [];

      return (
        <div
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDoubleClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <SubDetailTable
            tileTable={'Chi tiết thiết bị'}
            gridHeight={200}
            data={subData}
            columns={props.subColumns || []}
          />
        </div>
      );
    }

    const subData = propsSub.data.subData || [];
    return (
      // <SubDetailTable
      //   tileTable={propsSub.data.data.tileSubTable}
      //   gridHeight={200}
      //   data={subData}
      //   columns={props.subColumns || []}
      // />

      <div
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDoubleClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        // onMouseDown={e => e.stopPropagation()}
      >
        <SubDetailTable
          // onClickRowEventSub={onClickRowEventSub}
          tileTable={'Danh sách NV'}
          gridHeight={200}
          data={subData}
          columns={props.subColumns || []}
        />
      </div>
    );
  };
  const lookupData = useCallback(
    (e: any, sourceLookup?: any) => {
      const lookupData = sourceLookup
        ? sourceLookup
        : props?.data?.filter((obj, pos, arr) => {
            return (
              arr
                .map((mapObj: any) => mapObj[e?.lookupAt ? e.lookupAt : e?.dataField])
                .indexOf(obj[e.lookupAt ? e.lookupAt : e.dataField]) === pos
            );
          });

      return <Lookup dataSource={lookupData} valueExpr={e.dataField} displayExpr={e.dataField} />;
    },
    [props?.data]
  );

  const lookupDataNvThucHien = useCallback(
    (e: any) => {
      const lookupData = props?.data?.filter((obj, pos, arr) => {
        return (
          arr
            .map((mapObj: any) => mapObj[e?.lookupAt ? e.lookupAt : e?.dataField])
            .indexOf(obj[e.lookupAt ? e.lookupAt : e.dataField]) === pos
        );
      });
      // console.log({ lookupData });
      return <Lookup dataSource={lookupData} valueExpr={e.dataField} displayExpr={e.dataField} />;
    },
    [props?.data]
  );

  const data = useMemo(() => {
    return props.data;
  }, [props.data]);

  const columns = useMemo(() => {
    // xử lý trước khi render header table
    let amountGroupColPassed = 0;
    const columnsSettingSaved = tableSetting.tableColumnSetting.find(itemF => itemF.tableName == props.tableId);
    if (columnsSettingSaved) {
      // xử lý lại header theo setting saved
      // console.log('setting saved columns', columnsSettingSaved, props.columns);
      return props.columns.map((column: IColumnTable, index: number) => {
        if (!column.childHeader) {
          if (columnsSettingSaved.columnSize.length - 1 == index && columnsSettingSaved.hiddenAuto) {
            // ẩn cột khi cột này là cột cuối cùng có width là undefounded thì phải làm cho cột cuối cùng có width à  undefounded để fit width cho table
            return {
              ...column,
              width: undefined,
              visible: column.dataField && !columnsSettingSaved.columnHidden?.includes(column.dataField)
            };
          }
          return {
            ...column,
            width:
              column.width && columnsSettingSaved.columnSize[amountGroupColPassed + index]
                ? columnsSettingSaved.columnSize[amountGroupColPassed + index]
                : column.width,
            visible: column.dataField && !columnsSettingSaved.columnHidden?.includes(column.dataField)
          };
        } else {
          // khi đây là cột nhóm
          // console.log('group col', column, index);
          // để cập nhật khi cột khác phía sau k phải là cột nhóm
          amountGroupColPassed += column.childHeader?.length;
          return {
            ...column,
            visible: column.dataField && !columnsSettingSaved.columnHidden?.includes(column.dataField),
            childHeader: column.childHeader!.map((groupCol: IColumnTable, indexGC: number) => {
              // console.log('xử lý child bên trong', groupCol, indexGC);
              return {
                ...groupCol,
                width:
                  groupCol.width &&
                  columnsSettingSaved.columnSize[
                    index + (amountGroupColPassed - column.childHeader!.length!) + indexGC + 1
                  ]
                    ? columnsSettingSaved.columnSize[
                        index + (amountGroupColPassed - column.childHeader!.length!) + indexGC + 1
                      ]
                    : groupCol.width,
                visible: groupCol.dataField && !columnsSettingSaved.columnHidden?.includes(groupCol.dataField)
              };
            })
          };
          // xử lý cột ngoài ẩn hay hiện (không cần xử lý width) sau đó map bên trong
        }
      });
      // console.log('result columnsSaved', columnsSaved);
    }

    return props.columns;
  }, [props.columns]);

  const onRowPreparedStyle = useMemo(() => {
    if (props.onRowPreparedStyle) {
      return props.onRowPreparedStyle;
    }
  }, [props.onRowPreparedStyle]);

  const onCellPrepared = useMemo(() => {
    if (props.onCellPrepared) {
      return props.onCellPrepared;
    }
  }, [props.onCellPrepared]);

  const gridHeight = useMemo(() => {
    const heightScreen = window.innerHeight;
    const upHeight = props.downHeight ? (heightScreen > 920 ? 3 : heightScreen > 850 ? 2 : 0) : 0;
    if (heightScreen > 650) return props.downHeight ? 103 + upHeight! - props.downHeight : 103 + upHeight!;
    return props.downHeight ? 103 + upHeight! - props.downHeight : 103 + upHeight!;
  }, [props.downHeight]);

  // test data grid
  // return (
  //   <DataGrid dataSource={data}>
  //     {columns.map((e: any, key: number) => {
  //       if (e.childHeader) {
  //         return (
  //           <Column key={key} caption={e.caption}>
  //             {e.childHeader.map((e: any, key: number) => (
  //               <Column key={key + '_child'} dataField={e.dataField} caption={e.caption}></Column>
  //             ))}
  //           </Column>
  //         );
  //       }

  //       return <Column key={key + '_parent'} dataField={e.dataField} caption={e.caption}></Column>;
  //     })}
  //   </DataGrid>
  // );

  const resizingModes: ColumnResizeMode[] = ['nextColumn', 'widget'];

  const selectionConfig = useMemo(
    () => ({
      mode: props.selection?.mode || 'single',
      selectAllMode: props.selection?.selectAllMode || 'page',
      showCheckBoxesMode: props.selection?.showCheckBoxesMode || 'onClick',
      // THÊM DÒNG NÀY ĐỂ NÓ NHẬN GIÁ TRỊ TỪ FILE CHA TRUYỀN XUỐNG:
      groupSelectionMode: props.selection?.groupSelectionMode || 'none'
    }),
    [props.selection]
  );

  return (
    <>
      <TableSettingModal />
      <DataGridStyle
        onEditorPreparing={props.onEditorPreparing}
        ref={ref => props.onRightClickRow && (dataGrid.current = ref)}
        onSelectionChanged={props.onSelectionChanged}
        // filterValue={filterValue}
        isUseStyle={props.onRowPreparedStyle && props.onRowPreparedStyle}
        theme={selectedTheme}
        gridHeight={gridHeight}
        onInitialized={props.onInitialized}
        onRowHeight={data?.length !== 0 ? props.onRowHeight : undefined}
        repaintChangesOnly={props.repaintChangesOnly || false}
        wrapText={props.wrapText}
        // fontSize={tableSetting.fontSize ? tableSetting.fontSize : '11.5px'}
        fontSize={'11.5px'}
        fontStyle={tableSetting.fontStyle}
        disableHover={props.disableHover}
        wrapTextData={props.wrapTextData !== undefined ? props.wrapTextData : true}
        className={props.isModal ? props.isModal : 'gridContainer'}
        keyExpr={props.keyExpr || 'Id'}
        onExporting={onExporting}
        onRowUpdated={props.onRowUpdated} // <-- Kiểm tra xem props này tên là gì
        dataSource={data}
        columnMinWidth={props.columnMinWidth ? props.columnMinWidth : 40}
        columnResizingMode={
          props.activeNextColumn !== undefined && props.activeNextColumn === true
            ? resizingModes[0]
            : (
                props.columnResizingWidget
                  ? props.columnResizingWidget
                  : tableSetting.resizingMode == 'nextColumn'
                  ? resizingModes[0]
                  : resizingModes[1]
              )
            ? tableSetting.resizingMode == 'nextColumn'
              ? resizingModes[0]
              : resizingModes[1]
            : resizingModes[1]
        }
        columnAutoWidth={false}
        showBorders={props.showBorders ? props.showBorders : true}
        showColumnLines={true}
        rowAlternationEnabled={props.isStriped !== undefined ? props.isStriped : tableSetting.stripe}
        columnHidingEnabled={props.columnHidingEnabled ? props.columnHidingEnabled : false}
        showRowLines={props.showRowLines ? props.showRowLines : true}
        // showColumnHeaders={props.showColumnHeaders ? props.showColumnHeaders : true}
        allowColumnResizing={props.allowColumnResizing ? props.allowColumnResizing : true}
        // checkRole={props.checkRole}
        onRowDblClick={props.onDBClickRow && onDbClickRowEvent}
        onRowClick={props.onClickRow && onClickRowEvent}
        onContextMenuPreparing={props.onRightClickRow && onContextMenuPreparing}
        onRowPrepared={props.onRowPreparedStyle ? onRowPreparedStyle : undefined}
        onCellPrepared={props.onCellPrepared ? onCellPrepared : undefined}
        // Mouse cell
        // onCellHoverChanged={onMouseRowEvent} // tạm k dùng nữa
        hoverStateEnabled={props.hoverStateColor != undefined ? props.hoverStateColor : true}
        noDataText={t('Table.NoData')}
        onContentReady={(props.getCallbackExport || props.onPageInfo || props.onCallbackComponent) && onContentReady}
        onColumnsChange={onColumnsChanging}
        // showScrollbar={props.useScrollNative ? 'onScroll' : 'always'}
        selectedRowKeys={props.selectedRowKeys}
        autoNavigateToFocusedRow={false}
        syncLookupFilterValues={true}
        twoWayBindingEnabled={false}
        loadPanel={{
          enabled: props.enableLoadingPanel != undefined ? props.enableLoadingPanel : false,
          text: 'Đang tải dữ liệu..'
        }}
      >
        <Editing
          mode={props.modeEditing || 'cell'}
          allowUpdating={props.allowUpdating}
          allowDeleting={props.allowDeleting}
          allowAdding={props.allowAdding}
          texts={{
            addRow: 'Thêm',
            saveAllChanges: 'Lưu thay đổi',
            cancelAllChanges: 'Hoàn tác',
            saveRowChanges: 'Lưu',
            editRow: 'Cập nhật',
            cancelRowChanges: 'Hoàn tác',
            deleteRow: 'Xóa',
            confirmDeleteMessage: 'Bạn có chắc chắn muốn xóa dòng này không?',
            undeleteRow: 'Hoàn tác'
          }}
        />

        <Scrolling
          mode="standard"
          useNative={props.useScrollNative ? false : 'auto'} // sử dụng thanh scroll của bên ngoài => ảnh hưởng đến độ giật của table row
          scrollByContent={false}
        />

        {props.selection && <Selection {...selectionConfig} />}

        {props.summary && <Summary groupItems={props.summary || []} />}

        <FilterRow visible={props.isHiddenFilter === undefined || props.isHiddenFilter === false ? true : false} />

        <Sorting mode="multiple" />

        <ColumnFixing enabled={props.columnFix !== undefined ? props.columnFix : true} />

        <Export enabled={!props.isHiddenToolbar && (props.allowExcel !== undefined ? props.allowExcel : true)} />

        <ColumnChooser
          enabled={!props.isHiddenToolbar && true}
          mode="select"
          width={250}
          height={columns.length * 30 + 50 > 500 ? 500 : columns.length * 35 + 50}
        />

        <Paging
          enabled={!props.isHiddenPaging}
          defaultPageSize={props.minPageSize || (tableSetting.sizePage ? tableSetting.sizePage : MINPAGESIZE)}
        />

        <Grouping
          texts={{
            groupByThisColumn: 'text 1',
            groupContinuedMessage: t('Table.Group.ContinuedPage'),
            groupContinuesMessage: t('Table.Group.ContinuesPage'),
            ungroup: 'text 4',
            ungroupAll: 'text 5'
          }}
          autoExpandAll={props.expandedAll !== undefined ? props.expandedAll : undefined}
          expandMode={'rowClick'}
        />

        {/* đặt giới hạn nhỏ nhở min thì ẩn pager */}
        {!props.isHiddenPaging && (
          <Pager
            visible={
              (props.isHiddenPaging === undefined || props.isHiddenPaging === false) &&
              (props.data?.length >= minPageSize || props.noHiddenPaging)
            }
            allowedPageSizes={[
              minPageSize,
              minPageSize * 3,
              minPageSize * 8,
              minPageSize * 14,
              minPageSize * 20,
              minPageSize * 28
            ]}
            displayMode={'full'}
            showPageSizeSelector={props.showPageSizeSelector != undefined ? props.showPageSizeSelector : true}
            showInfo={props.showInfo != undefined ? props.showInfo : true}
            showNavigationButtons={props.showNavigationButtons != undefined ? props.showNavigationButtons : true}
            infoText={
              t('Table.Pagination.Show') +
              ' ' +
              (props.currentPage ? props.currentPage : '{0}') +
              ' ' +
              t('Table.Pagination.Of') +
              ' ' +
              (props.summaryPage ? props.summaryPage : '{1}') +
              ' ' +
              t('Table.Pagination.Page') +
              ' (' +
              `${props.total ? `${props.data?.length}/${props.total}` : `${props.data?.length}`}` +
              ' ' +
              t('Table.Pagination.Result') +
              ')'
            }
          />
        )}

        {!props.isHiddenToolbar && (
          <Toolbar>
            <Item
              location={'before'}
              // render={() => props.customBeforeToolbar && props.customBeforeToolbar}
              component={props.customBeforeToolbar && props.customBeforeToolbar}
            />
            <Item location={'center'} component={props.customCenterToolbar && props.customCenterToolbar} />
            {props.reload && (
              <Item>
                <div
                  onClick={() => props.oncClickReload && props.oncClickReload()}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide z-[100]"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Reload table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Reload table"
                  >
                    <SyncOutlined size={34} className="p-[10px]" />
                  </div>
                </div>
              </Item>
            )}

            {props.thuTien && (
              <Item>
                <div
                  // Kiểm tra logic: Nếu có item được chọn thì cho phép gọi hàm, ngược lại không làm gì
                  onClick={() => props.listThuTien > 0 && props.onClickThuTien && props.onClickThuTien()}
                  className={`dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide z-[100] ${
                    props.listThuTien > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed pointer-events-none' // Chặn click và đổi icon chuột
                  }`}
                >
                  <div
                    className={`dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button
                    ${props.listThuTien > 0 ? '' : 'opacity-50'}`} // Làm mờ nút khi bị disable
                    role="button"
                    aria-label="Thu tiền"
                    aria-haspopup="dialog"
                    title={props.listThuTien > 0 ? 'Thu tiền' : 'Vui lòng chọn đơn hàng'}
                  >
                    <MdPayments
                      size={34}
                      className={`p-[10px] transition-colors ${
                        props.listThuTien > 0 ? 'text-green-600' : 'text-gray-400'
                      }`}
                    />
                  </div>
                </div>
              </Item>
            )}

            {props.wallet && (
              <Item>
                <div
                  onClick={() => props.onClickCapPhat && props.onClickCapPhat()}
                  className={`dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide`}
                >
                  <div
                    className={`dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button`}
                    role="button"
                    aria-label="Addnew table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Tạo lệnh"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4.5px 8px',
                      gap: '6px',
                      height: '34px',
                      backgroundColor: '#389e0d',
                      color: '#ffffff',
                      border: '1px solid #389e0d',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#237804')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#389e0d')}
                  >
                    <DeliveredProcedureOutlined size={24} />
                    <span>{t('Tạo lệnh')}</span>
                  </div>
                </div>
              </Item>
            )}

            {props.isAdmin && (
              <Item>
                <div
                  onClick={() => props.onClickDelete && props.onClickDelete()}
                  className={`dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide`}
                >
                  <div
                    className={`dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button`}
                    role="button"
                    aria-label="Addnew table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Xóa sản phẩm"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4.5px 8px',
                      gap: '6px',
                      height: '34px',
                      backgroundColor: '#fff1f0',
                      color: '#cf1322',
                      border: '1px solid #ffa39e',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      transition: 'background 0.2s'
                    }}
                  >
                    <DeleteOutlined size={24} />
                    <span>{t('Xóa sản phẩm')}</span>
                  </div>
                </div>
              </Item>
            )}

            {props.collectMoney && (
              <Item>
                <div
                  onClick={() => props.oncClickReload && props.oncClickReload()}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide z-[100]"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Reload table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Reload table"
                  >
                    <SyncOutlined size={34} className="p-[10px]" />
                  </div>
                </div>
              </Item>
            )}
            {props.showReloadCustomer && (
              <Item>
                <div className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide z-[100]">
                  <Tooltip title={t('OrganizedSales.OrderManager.Reload')}>
                    <button
                      onClick={() => dispatch(updateReqReGetCustomer(true))}
                      className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg hover:text-blue-500 hover:bg-gray-100 transition"
                    >
                      <SyncOutlined rev={''} style={{ fontSize: '18px' }} />
                    </button>
                  </Tooltip>
                </div>
              </Item>
            )}

            {props.showImportExcel && (
              <Item>
                {/* <div className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide">
                  {' '}
                  <Tooltip title={t('DataManagement.Customer.ImportModal.Title')} placement="top">
                    <button
                      onClick={() => props.setOpenImportXls(true)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-black transition"
                    >
                      <DeliveredProcedureOutlined rev={''} className="text-[#337AB7]" />
                      <span className="text-sm font-medium">Import</span>
                    </button>
                  </Tooltip>
                </div> */}

                <div
                  onClick={() => props.setOpenImportXls && props.setOpenImportXls(true)}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Addnew table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Addnew records"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4.5px 8px',
                      gap: '4px',
                      height: '34px'
                    }}
                  >
                    <DeliveredProcedureOutlined size={24} />
                    <span>{t('Table.Tooltip.Import')}</span>
                  </div>
                </div>
              </Item>
            )}

            {props.showExportExcel && (
              <Item>
                {/* <div className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide">
                  {' '}
                  <Tooltip title={t('DataManagement.Customer.ImportModal.Title')} placement="top">
                    <button
                      onClick={() => props.setOpenImportXls(true)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-black transition"
                    >
                      <DeliveredProcedureOutlined rev={''} className="text-[#337AB7]" />
                      <span className="text-sm font-medium">Import</span>
                    </button>
                  </Tooltip>
                </div> */}

                <div
                  onClick={() => props.handleExportExcel && props.handleExportExcel()}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Addnew table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Addnew records"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4.5px 8px',
                      gap: '4px',
                      height: '34px'
                    }}
                  >
                    <DeliveredProcedureOutlined size={24} />
                    <span>{t('Table.Tooltip.ExportEx')}</span>
                  </div>
                </div>
              </Item>
            )}
            {!props.hiddenAddButton && (
              <Item>
                <div
                  onClick={() => props.onClickAddRecord && props.onClickAddRecord()}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Addnew table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Addnew records"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4.5px 8px',
                      gap: '4px',
                      height: '34px'
                    }}
                  >
                    <MdOutlineAddCircleOutline size={24} />
                    <span>{t('Table.Tooltip.Create')}</span>
                  </div>
                </div>
              </Item>
            )}

            <Item name="columnChooserButton" />
            <Item name="exportButton" />
            {!props.hiddenSetting && (
              <Item>
                <div
                  onClick={() => onClickSettingTable()}
                  className="dx-item dx-toolbar-item dx-toolbar-button dx-toolbar-item-auto-hide dx-toolbar-text-auto-hide"
                >
                  <div
                    className="dx-widget dx-button dx-button-mode-contained dx-button-normal dx-button-has-text dx-button-has-icon dx-datagrid-toolbar-button dx-datagrid-column-chooser-button"
                    role="button"
                    aria-label="Setting table"
                    aria-haspopup="dialog"
                    // tabIndex="0"
                    title="Setting table"
                  >
                    <HiOutlineCog size={34} className="p-2" />
                  </div>
                </div>
              </Item>
            )}
          </Toolbar>
        )}

        {columns.map((e: any, key: number) => {
          if (e.childHeader) {
            return (
              <Column key={key} caption={t(e.caption)} dataField={e.dataField} visible={e.visible}>
                {e.childHeader.map((e: any, key: number) => (
                  <Column
                    key={key}
                    type={e.type ? e.type : undefined}
                    dataField={e.dataField}
                    caption={t(e.caption)}
                    cellRender={e.cellRender && e.cellRender}
                    cellTemplate={e.cellTemplate && e.cellTemplate}
                    groupCellRender={e.groupCellRender && e.groupCellRender}
                    // showWhenGrouped={false}
                    width={e.width}
                    minWidth={e.minWidth}
                    visible={e.visible}
                    allowHiding={e.allowHiding ? e.allowHiding : true}
                    showInColumnChooser={e.showInColumnChooser != undefined ? e.showInColumnChooser : true}
                    // visibleWidth={e.visibleWidth}
                    dataType={e.dataType ? e.dataType : undefined}
                    fixed={e.fixed !== undefined ? e.fixed : undefined}
                    fixedPosition={e.fixedPosition ? e.fixedPosition : 'left'}
                    format={e.format}
                    allowFiltering={e.allowFiltering}
                    headerFilter={e.headerFilter}
                    defaultSortOrder={e.defaultSortOrder && e.defaultSortOrder}
                    // defaultSortIndex={e.defaultSortIndex && e.defaultSortIndex}
                    allowSorting={e.allowSorting}
                    alignment={e.alignment ? e.alignment : 'left'}
                    editCellRender={e.allowEditing && e.editCellRender}
                    allowEditing={e.allowEditing ? true : false}
                    groupIndex={e.groupIndex == 0 ? e.groupIndex : e.groupIndex}
                  >
                    {e.lookupFilter && lookupData(e)}
                    {e.lookupFilterNvThucHien && lookupDataNvThucHien(e)}
                    {e.headerFilter && <HeaderFilter visible={true} dataSource={e.dataFilter} />}
                  </Column>
                ))}
              </Column>
            );
          }

          return (
            <Column
              // cssClass="warning_cell"
              key={key}
              type={e.type ? e.type : undefined}
              dataField={e.dataField}
              caption={t(e.caption)}
              headerCellRender={e.headerRender ? e.headerRender : undefined}
              cellRender={e.cellRender && e.cellRender}
              cellTemplate={e.cellTemplate && e.cellTemplate}
              groupCellRender={e.groupCellRender && e.groupCellRender}
              // showWhenGrouped={false}
              // cellComponent={e.isPicture && cellRender}
              dataType={e.dataType ? e.dataType : undefined}
              width={e.width}
              minWidth={e.minWidth}
              visible={e.visible}
              allowHiding={e.allowHiding ? e.allowHiding : true}
              showInColumnChooser={e.showInColumnChooser != undefined ? e.showInColumnChooser : true}
              // visibleWidth={e.visibleWidth}
              fixed={e.fixed ? e.fixed : undefined}
              fixedPosition={e.fixedPosition ? e.fixedPosition : 'left'}
              format={e.format}
              allowFiltering={e.allowFiltering}
              headerFilter={e.headerFilter}
              defaultSortOrder={e.defaultSortOrder && e.defaultSortOrder}
              // defaultSortIndex={e.defaultSortIndex && e.defaultSortIndex}
              allowSorting={e.allowSorting}
              alignment={e.alignment ? e.alignment : 'left'}
              editCellRender={e.allowEditing && e.editCellRender}
              allowEditing={e.allowEditing ? true : false}
              groupIndex={e.groupIndex == 0 ? e.groupIndex : e.groupIndex}
            >
              {e.lookupFilter && lookupData(e, e.sourceLookup || undefined)}
              {e.lookupFilterNvThucHien && lookupDataNvThucHien(e)}
              {e.headerFilter && <HeaderFilter visible={true} dataSource={e.dataFilter} />}
            </Column>
          );
        })}

        {props?.isSubRowRender && (
          <MasterDetail
            enabled={true}
            autoExpandAll={false}
            render={(propsRender: any) => SubDetailCallBack(propsRender)}
          />
        )}

        {/* xử lý bằng cách gọi hàm SubDetailTable này ngược lại cho main table */}

        {/* Số thứ tự  mặc định nhất*/}
        {/* Hình ảnh */}
        {/* Lọc theo selectBox */}
        {/* Định dạng hiển thị ngày */}
        {/* Định dạng ghi chú cần có width */}
        {/* Định dạng lọc theo từng khoản cho số tiền */}
        {/* Định dạng các nút chức năng */}

        {props.data.length > 0 && (
          <Summary recalculateWhileEditing={false} skipEmptyValues={false}>
            {props.columns.map((e: any, key: number) => {
              if (e.childHeader) {
                return e.childHeader.map((e: any, key: number) => {
                  if (e.isSumFooter) {
                    return (
                      <GroupItem
                        key={key}
                        name={e.dataField}
                        valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                        displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                        column={e.sumCol ? e.sumCol : e.dataField}
                        summaryType={e.summaryType ? e.summaryType : 'sum'}
                        showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                        alignByColumn={e.dataField}
                        showInGroupFooter={e.showInGroupFooter ? e.showInGroupFooter : undefined}
                      />
                    );
                  }
                  if (e.isSumCol) {
                    return (
                      <TotalItem
                        key={key}
                        name="RowsSumCount0"
                        valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                        displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                        column={e.sumCol ? e.sumCol : e.dataField}
                        summaryType={e.summaryType ? e.summaryType : 'sum'}
                        showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                        customizeText={e.customizeText ? e.customizeText : undefined}
                      />
                    );
                  }
                });
              }

              if (e.isSumFooter) {
                return (
                  <GroupItem
                    key={key}
                    valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                    displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                    column={e.sumCol ? e.sumCol : e.dataField}
                    summaryType={e.summaryType ? e.summaryType : 'sum'}
                    showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                    alignByColumn={e.dataField}
                    showInGroupFooter={e.showInGroupFooter ? e.showInGroupFooter : undefined}
                  ></GroupItem>
                );
              }

              if (e.isSumCol)
                return (
                  <TotalItem
                    key={key}
                    name="RowsSumCount2"
                    valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                    displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                    column={e.sumCol ? e.sumCol : e.dataField}
                    summaryType={e.summaryType ? e.summaryType : 'sum'}
                    showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                    customizeText={e.customizeText ? e.customizeText : undefined}
                  />
                );
            })}

            {props.columns.map((e: any, key: number) => {
              if (e.childHeader) {
                return e.childHeader.map((e: any, key: number) => {
                  if (e.isSumCol && e.isSumFooter) {
                    return (
                      <TotalItem
                        key={key}
                        name="RowsSumCount3"
                        valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                        displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                        column={e.sumCol ? e.sumCol : e.dataField}
                        summaryType={e.summaryType ? e.summaryType : 'sum'}
                        showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                        customizeText={e.customizeText ? e.customizeText : undefined}
                      />
                    );
                  }
                });
              }

              if (e.isSumCol && e.isSumFooter)
                return (
                  <TotalItem
                    key={key + '_aSum'}
                    name="RowsSumCount4"
                    valueFormat={e.formatCustomSum ? e.formatCustomSum : e.valueFormat}
                    displayFormat={e.displayFormat ? e.displayFormat : '{0}'}
                    column={e.sumCol ? e.sumCol : e.dataField}
                    summaryType={e.summaryType ? e.summaryType : 'sum'}
                    showInColumn={e.showInColumn ? e.showInColumn : e.dataField}
                  />
                );
            })}
          </Summary>
        )}
      </DataGridStyle>
    </>
  );

  // new DataGrid(container, {
  //   accessKey:undefined,
  //   activeStateEnabled:false,
  //   allowColumnReordering:false,
  //   allowColumnResizing:false,
  //   autoNavigateToFocusedRow:true,
  //   cacheEnabled:true,
  //   cellHintEnabled:true,
  //   columnAutoWidth:false,
  //   columnChooser: {
  //   allowSearch:false,
  //   emptyPanelText:"Drag a column here to hide it",
  //   enabled:false,
  //   height:260,
  //   mode:"dragAndDrop",
  //   searchTimeout:500,
  //   sortOrder:undefined,
  //   title:"Column Chooser",
  //   width:250
  //   },
  //   columnFixing: {},
  //   columnHidingEnabled:false,
  //   columnMinWidth:undefined,
  //   columnResizingMode:"nextColumn",
  //   columns: [
  //   {
  //   alignment:undefined,
  //   allowEditing:true,
  //   allowExporting:true,
  //   allowFiltering:true,
  //   allowFixing:true,
  //   allowGrouping:true,
  //   allowHeaderFiltering:true,
  //   allowHiding:true,
  //   allowReordering:true,
  //   allowResizing:true,
  //   allowSearch:true,
  //   allowSorting:true,
  //   autoExpandGroup:true,
  //   buttons: [],
  //   calculateCellValue:null,
  //   calculateDisplayValue:null,
  //   calculateFilterExpression:null,
  //   calculateGroupValue:null,
  //   calculateSortValue:null,
  //   caption:undefined,
  //   cellComponent:null,
  //   cellRender:null,
  //   cellTemplate:null,
  //   columns:undefined,
  //   cssClass:undefined,
  //   customizeText:null,
  //   dataField:undefined,
  //   dataType:undefined,
  //   editCellComponent:null,
  //   editCellRender:null,
  //   editCellTemplate:null,
  //   editorOptions:null,
  //   encodeHtml:true,
  //   falseText:false,
  //   filterOperations:undefined,
  //   filterType:"include",
  //   filterValue:undefined,
  //   filterValues:undefined,
  //   fixed:false,
  //   fixedPosition:undefined,
  //   format:"",
  //   formItem:null,
  //   groupCellComponent:null,
  //   groupCellRender:null,
  //   groupCellRender:null,
  //   groupIndex:undefined,
  //   headerCellComponent:null,
  //   headerCellRender:null,
  //   headerCellTemplate:null,
  //   headerFilter: {},
  //   hidingPriority:undefined,
  //   isBand:undefined,
  //   lookup: {},
  //   minWidth:undefined,
  //   name:undefined,
  //   ownerBand:undefined,
  //   renderAsync:false,
  //   selectedFilterOperation:undefined,
  //   setCellValue:null,
  //   showEditorAlways:false,
  //   showInColumnChooser:true,
  //   showWhenGrouped:false,
  //   sortIndex:undefined,
  //   sortingMethod:undefined,
  //   sortOrder:undefined,
  //   trueText:true,
  //   type:null,
  //   validationRules:null,
  //   visible:true,
  //   visibleIndex:undefined,
  //   width:undefined
  //   }
  //   ],
  //   columnWidth:undefined,
  //   customizeColumns:null,
  //   dataRowComponent:null,
  //   dataRowRender:null,
  //   dataRowTemplate:null,
  //   dataSource:Edit,
  //   dateSerializationFormat:null,
  //   disabled:false,
  //   editing: {},
  //   elementAttr:{},
  //   errorRowEnabled:true,
  //   export: {},
  //   filterBuilder:{},
  //   filterBuilderPopup:{},
  //   filterPanel: {},
  //   filterRow: {},
  //   filterSyncEnabled:"auto",
  //   filterValue:null,
  //   focusedColumnIndex:-1,
  //   focusedRowEnabled:false,
  //   focusedRowIndex:-1,
  //   focusedRowKey:undefined,
  //   grouping: {},
  //   groupPanel: {},
  //   headerFilter: {},
  //   height:undefined,
  //   highlightChanges:false,
  //   hint:undefined,
  //   hoverStateEnabled:false,
  //   keyboardNavigation: {},
  //   keyExpr:undefined,
  //   loadPanel: {},
  //   masterDetail: {},
  //   noDataText:"No data",
  //   onAdaptiveDetailRowPreparing:null,
  //   onCellClick:null,
  //   onCellDblClick:null,
  //   onCellHoverChanged:null,
  //   onCellPrepared:null,
  //   onContentReady:null,
  //   onContextMenuPreparing:null,
  //   onDataErrorOccurred:null,
  //   onDisposing:null,
  //   onEditCanceled:null,
  //   onEditCanceling:null,
  //   onEditingStart:null,
  //   onEditorPrepared:null,
  //   onEditorPreparing:null,
  //   onExporting:null,
  //   onFocusedCellChanged:null,
  //   onFocusedCellChanging:null,
  //   onFocusedRowChanged:null,
  //   onFocusedRowChanging:null,
  //   onInitialized:null,
  //   onInitNewRow:null,
  //   onKeyDown:null,
  //   onOptionChanged:null,
  //   onRowClick:null,
  //   onRowCollapsed:null,
  //   onRowCollapsing:null,
  //   onRowDblClick:null,
  //   onRowExpanded:null,
  //   onRowExpanding:null,
  //   onRowInserted:null,
  //   onRowInserting:null,
  //   onRowPrepared:null,
  //   onRowRemoved:null,
  //   onRowRemoving:null,
  //   onRowUpdated:null,
  //   onRowUpdating:null,
  //   onRowValidating:null,
  //   onSaved:null,
  //   onSaving:null,
  //   onSelectionChanged:null,
  //   onToolbarPreparing:null,
  //   pager: {},
  //   paging: {},
  //   remoteOperations: {},
  //   renderAsync:false,
  //   repaintChangesOnly:false,
  //   rowAlternationEnabled:false,
  //   rowDragging: {},
  //   rtlEnabled:false,
  //   scrolling: {
  //   columnRenderingMode:"standard",
  //   mode:"standard",
  //   preloadEnabled:false,
  //   renderAsync:undefined,
  //   rowRenderingMode:"standard",
  //   scrollByContent:true,
  //   scrollByThumb:false,
  //   showScrollbar:"onHover",
  //   useNative:"auto"
  //   },
  //   searchPanel: {},
  //   selectedRowKeys:null,
  //   selection: {},
  //   selectionFilter:[],
  //   showBorders:false,
  //   showColumnHeaders:true,
  //   showColumnLines:true,
  //   showRowLines:false,
  //   sortByGroupSummaryInfo: [],
  //   sorting: {},
  //   stateStoring: {},
  //   summary: {
  //   calculateCustomSummary:null,
  //   groupItems: [],
  //   recalculateWhileEditing:false,
  //   skipEmptyValues:true,
  //   texts: {},
  //   totalItems: [
  //   {
  //   alignment:undefined,
  //   column:undefined,
  //   cssClass:undefined,
  //   customizeText:null,
  //   displayFormat:undefined,
  //   name:undefined,
  //   showInColumn:undefined,
  //   skipEmptyValues:null,
  //   summaryType:undefined,
  //   valueFormat:undefined
  //   }
  //   ],
  //   },
  //   syncLookupFilterValues:true,
  //   tabIndex:0,
  //   toolbar: {},
  //   twoWayBindingEnabled:true,
  //   visible:true,
  //   width:undefined,
  //   wordWrapEnabled:false
  //   });
}
